import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { Link } from 'react-router-dom';
import { FiX, FiSearch, FiAlertTriangle, FiBriefcase, FiUser, FiMapPin, FiCalendar } from 'react-icons/fi';
import '../styles/ManagerDashboard.css';
import '../styles/Submissions.css';

const MissingSubmissionsModal = ({ startDate, endDate, region, client, creationType, onClose }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchMissingJobs = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BASE_URL}/api/recruitment/applications/job-report/missing-submissions`,
          {
            params: {
              startDate,
              endDate,
              region: region || 'Onsite',
              client: client ? (typeof client === 'object' ? client.name : client) : 'all',
              creationType: creationType || 'all'
            },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setJobs(response.data || []);
      } catch (err) {
        console.error('Error fetching missing submission jobs:', err);
        setError('Failed to load missing submission jobs.');
      } finally {
        setLoading(false);
      }
    };

    fetchMissingJobs();
  }, [startDate, endDate, region, client, creationType]);

  const filteredJobs = useMemo(() => {
    if (!searchQuery.trim()) return jobs;
    const q = searchQuery.toLowerCase().trim();
    return jobs.filter(j => 
      (j.clientName && j.clientName.toLowerCase().includes(q)) ||
      (j.jobTitle && j.jobTitle.toLowerCase().includes(q)) ||
      (j.jobId && j.jobId.toLowerCase().includes(q)) ||
      (j.externalJobRef && j.externalJobRef.toLowerCase().includes(q)) ||
      (j.recruiterName && j.recruiterName.toLowerCase().includes(q)) ||
      (j.roleOwnerName && j.roleOwnerName.toLowerCase().includes(q)) ||
      (j.location && j.location.toLowerCase().includes(q))
    );
  }, [jobs, searchQuery]);

  const formatDateRange = () => {
    const s = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const e = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} — ${e}`;
  };

  const formatCreatedAt = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const clientDisplayName = client ? (typeof client === 'object' ? client.name : client) : 'All Clients';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-panel" 
        style={{ maxWidth: '1050px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} 
        onClick={ev => ev.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header" style={{ background: '#fef2f2', borderBottom: '1px solid #fee2e2' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiAlertTriangle color="#dc2626" size={20} />
              <h3 className="modal-title" style={{ color: '#991b1b', margin: 0 }}>
                Jobs Missing Submissions ({jobs.length} Open Roles)
              </h3>
            </div>
            <p className="modal-subtitle" style={{ color: '#7f1d1d', marginTop: '4px' }}>
              Period: <strong>{formatDateRange()}</strong> | Region: <strong>{region || 'Onsite'}</strong> | Client: <strong>{clientDisplayName}</strong>
            </p>
          </div>
          <button className="modal-close-btn" onClick={onClose}><FiX size={20} /></button>
        </div>

        {/* Search & Filter Bar */}
        <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '260px' }}>
            <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={16} />
            <input
              type="text"
              placeholder="Search by job title, client, recruiter, or ID..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
            Showing <strong>{filteredJobs.length}</strong> of {jobs.length} unsubmitted positions
          </div>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div className="modal-loading" style={{ padding: '40px' }}>
              <div className="modal-spinner" />
              <p>Loading jobs without submissions...</p>
            </div>
          ) : error ? (
            <div className="modal-error">{error}</div>
          ) : filteredJobs.length === 0 ? (
            <div className="modal-empty" style={{ padding: '40px', textAlign: 'center' }}>
              <FiBriefcase size={36} color="#94a3b8" style={{ marginBottom: '12px' }} />
              <p style={{ fontSize: '15px', color: '#475569', fontWeight: 600 }}>
                {searchQuery ? 'No jobs match your search.' : 'Great job! No jobs are missing submissions in this period.'}
              </p>
            </div>
          ) : (
            <div className="modal-table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="submissions-table" style={{ fontSize: '13px', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ minWidth: '130px' }}>Client</th>
                    <th style={{ minWidth: '100px' }}>Job ID</th>
                    <th style={{ minWidth: '120px' }}>External Ref</th>
                    <th style={{ minWidth: '180px' }}>Job Title (Role)</th>
                    <th style={{ minWidth: '140px' }}>Assigned Recruiter</th>
                    <th style={{ minWidth: '120px' }}>Location</th>
                    <th style={{ minWidth: '110px' }}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map((row, idx) => (
                    <tr 
                      key={row.id || idx}
                      style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}
                    >
                      <td className="client-name" style={{ fontWeight: 600 }}>{row.clientName}</td>
                      <td style={{ fontSize: '11px', color: '#64748b' }}>{row.jobId}</td>
                      <td style={{ fontSize: '12px', fontWeight: 500, color: '#3b5bfd' }}>{row.externalJobRef}</td>
                      <td>
                        <Link
                          to={`/submissions/${row.id}`}
                          state={{ jobInfo: row }}
                          className="job-title-link"
                          onClick={onClose}
                          style={{ fontWeight: 600, color: '#0284c7' }}
                        >
                          {row.jobTitle}
                        </Link>
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <FiUser size={12} color="#64748b" /> {row.recruiterName}
                        </span>
                      </td>
                      <td className="na-text" style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.location}>
                        {row.location}
                      </td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>
                        {formatCreatedAt(row.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && (
          <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              <strong>{filteredJobs.length}</strong> jobs pending candidate submissions
            </span>
            <button 
              className="modal-close-footer-btn" 
              onClick={onClose}
              style={{ padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MissingSubmissionsModal;
